<template>
  <gc-main-layout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :formsConfig="formsConfig"
    :selectedIndex="selectedIndex"
    :backButtonEnable="true"
    :backButtonFunction="backButtonFunction"
  >
    <template #headerContent>
      <GcHeaderContentStatic title="Menu Management" />
    </template>

    <DxToolbar class="view-toolbar">
      <DxItem location="before" locate-in-menu="never">
        <template #default>
          <DxButton
            icon="plus"
            text="Add L1 Item"
            type="default"
            styling-mode="contained"
            @click="openAddNode(null)"
          />
        </template>
      </DxItem>
      <DxItem location="before" locate-in-menu="never" :visible="!!(selectedNode && nodeLevel(selectedNode) === 1)">
        <template #default>
          <DxButton
            icon="plus"
            text="Add L2 Item"
            styling-mode="outlined"
            @click="openAddNode(selectedNode)"
          />
        </template>
      </DxItem>
      <DxItem location="before" locate-in-menu="never" :visible="!!(selectedNode && nodeLevel(selectedNode) === 2)">
        <template #default>
          <DxButton
            icon="plus"
            text="Add Tab"
            styling-mode="outlined"
            @click="openAddNode(selectedNode)"
          />
        </template>
      </DxItem>
    </DxToolbar>

    <div class="mm-layout" :style="layoutHeight ? { height: layoutHeight } : {}">
      <!-- ── Left: Navigation tree ── -->
      <div class="mm-tree-panel">
        <div class="mm-tree-title">Navigation Tree</div>

        <div v-if="!formsConfig.loadingVisible && rootNodes.length === 0" class="mm-empty">
          No menu items found. Add an L1 item to get started.
        </div>

        <draggable
          :value="rootNodes"
          group="mm-l1"
          handle=".mm-drag-handle"
          :animation="150"
          @end="(e) => onDragEnd(null, e)"
        >
          <div
            v-for="l1 in rootNodes"
            :key="l1.MenuItemId"
            class="mm-l1-block"
          >
            <!-- L1 row -->
            <div
              :class="['mm-node mm-node--l1', { 'mm-node--selected': selectedNode && selectedNode.MenuItemId === l1.MenuItemId, 'mm-node--inactive': l1.Inactive === 'Y' }]"
              @click="selectNode(l1)"
            >
              <i class="dx-icon-dragvertical mm-drag-handle" @click.stop />
              <i
                :class="l1Expanded(l1) ? 'dx-icon-chevrondown' : 'dx-icon-chevronnext'"
                class="mm-chevron"
                @click.stop="toggleL1(l1)"
              />
              <i v-if="l1.Icon" :class="l1.Icon" class="mm-node-icon" />
              <span class="mm-node-label">{{ l1.Label }}</span>
              <span v-if="l1.Inactive === 'Y'" class="mm-badge mm-badge--inactive">Inactive</span>
              <span class="mm-node-seq">{{ l1.Sequence }}</span>
            </div>

            <!-- L2 rows -->
            <div v-if="l1Expanded(l1)">
              <draggable
                :value="childrenOf(l1.MenuItemId)"
                :group="'mm-l2-' + l1.MenuItemId"
                handle=".mm-drag-handle"
                :animation="150"
                @end="(e) => onDragEnd(l1.MenuItemId, e)"
              >
                <div
                  v-for="l2 in childrenOf(l1.MenuItemId)"
                  :key="l2.MenuItemId"
                >
                  <div
                    :class="['mm-node mm-node--l2', { 'mm-node--selected': selectedNode && selectedNode.MenuItemId === l2.MenuItemId, 'mm-node--inactive': l2.Inactive === 'Y' }]"
                    @click="selectNode(l2)"
                  >
                    <i class="dx-icon-dragvertical mm-drag-handle" @click.stop />
                    <i
                      :class="l2Expanded(l2) ? 'dx-icon-chevrondown' : 'dx-icon-chevronnext'"
                      class="mm-chevron"
                      @click.stop="toggleL2(l2)"
                    />
                    <span class="mm-node-label">{{ l2.Label }}</span>
                    <span v-if="l2.Inactive === 'Y'" class="mm-badge mm-badge--inactive">Inactive</span>
                    <span class="mm-node-seq">{{ l2.Sequence }}</span>
                  </div>

                  <!-- L3 tab rows -->
                  <div v-if="l2Expanded(l2)">
                    <draggable
                      :value="childrenOf(l2.MenuItemId)"
                      :group="'mm-l3-' + l2.MenuItemId"
                      handle=".mm-drag-handle"
                      :animation="150"
                      @end="(e) => onDragEnd(l2.MenuItemId, e)"
                    >
                      <div
                        v-for="l3 in childrenOf(l2.MenuItemId)"
                        :key="l3.MenuItemId"
                        :class="['mm-node mm-node--l3', { 'mm-node--selected': selectedNode && selectedNode.MenuItemId === l3.MenuItemId, 'mm-node--inactive': l3.Inactive === 'Y' }]"
                        @click="selectNode(l3)"
                      >
                        <i class="dx-icon-dragvertical mm-drag-handle" @click.stop />
                        <span class="mm-node-label">{{ l3.Label }}</span>
                        <span v-if="l3.FeatureCode" class="mm-badge mm-badge--feature">{{ l3.FeatureCode }}</span>
                        <span v-if="l3.Inactive === 'Y'" class="mm-badge mm-badge--inactive">Inactive</span>
                        <span class="mm-node-seq">{{ l3.Sequence }}</span>
                      </div>
                    </draggable>
                  </div>
                </div>
              </draggable>
            </div>
          </div>
        </draggable>
      </div>

      <!-- ── Right: Editor panel ── -->
      <div class="mm-editor-panel">
        <div v-if="!editorNode" class="mm-editor-empty">
          Select a node from the tree to edit it, or use the toolbar to add a new item.
        </div>

        <div v-if="editorNode" class="mm-editor-form">
          <div class="mm-editor-title">
            {{ isNewNode ? 'New Item' : 'Edit Node' }}
          </div>

          <div class="mm-field">
            <label class="mm-label">Label <span class="mm-required">*</span></label>
            <DxTextBox
              v-model="editorNode.Label"
              styling-mode="outlined"
              placeholder="Display label"
            />
          </div>

          <div class="mm-field">
            <label class="mm-label">Parent</label>
            <DxSelectBox
              :data-source="parentOptions"
              display-expr="label"
              value-expr="value"
              v-model="editorNode.ParentMenuItemId"
              styling-mode="outlined"
              :read-only="!isNewNode && nodeLevel(editorNode) === 3"
            />
          </div>

          <div class="mm-field">
            <label class="mm-label">Icon <span class="mm-hint">(L1 items only)</span></label>
            <DxTextBox
              v-model="editorNode.Icon"
              styling-mode="outlined"
              placeholder="e.g. icon-General-Ledger"
            />
          </div>

          <div class="mm-field">
            <label class="mm-label">Path <span class="mm-hint">(leaf / tab items)</span></label>
            <DxTextBox
              v-model="editorNode.Path"
              styling-mode="outlined"
              placeholder="e.g. /customer"
            />
          </div>

          <div class="mm-field">
            <label class="mm-label">Feature Code <span class="mm-hint">(tab / leaf items only)</span></label>
            <DxSelectBox
              :data-source="featureCodes"
              :display-expr="featureCodeDisplay"
              value-expr="Code"
              v-model="editorNode.FeatureCode"
              styling-mode="outlined"
              :search-enabled="true"
              search-mode="contains"
              :search-expr="['Code', 'DisplayName']"
              :show-clear-button="true"
              placeholder="Search feature code..."
            />
          </div>

          <div class="mm-field">
            <label class="mm-label">Sequence</label>
            <DxNumberBox
              v-model="editorNode.Sequence"
              styling-mode="outlined"
              :min="0"
            />
          </div>

          <div class="mm-field mm-field--inline">
            <label class="mm-label">Active</label>
            <DxSwitch
              :value="editorNode.Inactive !== 'Y'"
              @value-changed="onActiveToggle($event)"
            />
          </div>

          <div v-if="!isNewNode" class="mm-field mm-render-info">
            <span class="mm-label">Children render as:</span>
            <span class="mm-render-badge">{{ childRenderMode(editorNode) }}</span>
          </div>

          <div class="mm-editor-actions">
            <DxButton
              text="Cancel"
              styling-mode="outlined"
              @click="cancelEdit"
            />
            <DxButton
              v-if="!isNewNode"
              text="Delete"
              type="danger"
              styling-mode="outlined"
              @click="confirmDelete"
            />
            <DxButton
              v-if="!isNewNode && nodeLevel(editorNode) === 1"
              icon="plus"
              text="Add L2 Item"
              type="normal"
              styling-mode="outlined"
              @click="openAddNode(selectedNode)"
            />
            <DxButton
              v-if="!isNewNode && nodeLevel(editorNode) === 2"
              icon="plus"
              text="Add Tab"
              type="normal"
              styling-mode="outlined"
              @click="openAddNode(selectedNode)"
            />
            <DxButton
              text="Save"
              type="default"
              styling-mode="contained"
              @click="saveNode"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Delete confirmation popup -->
    <DxPopup
      :visible.sync="deletePopup.visible"
      title="Confirm Delete"
      :width="400"
      :height="180"
      :drag-enabled="false"
      :show-close-button="true"
      :hide-on-outside-click="false"
      :wrapperAttr="{ class: 'css-v2 gc-popup' }"
    >
      <div class="mm-confirm-body">
        <p>{{ deletePopup.message }}</p>
        <div class="mm-confirm-actions">
          <DxButton text="Cancel" styling-mode="outlined" @click="deletePopup.visible = false" />
          <DxButton text="Delete" type="danger" styling-mode="contained" @click="deleteNode" />
        </div>
      </div>
    </DxPopup>
  </gc-main-layout>
</template>

<script>
import { mapGetters } from "vuex";
import BaseViewMixin from "../../../v1/_mixins/base.view";
import { MenuManagementService } from "../../../_services/api/settings/graycorp/menu-management.service";
import draggable from "vuedraggable";

export default {
  components: { draggable },
  mixins: [BaseViewMixin],

  data() {
    return {
      allItems: [],
      featureCodes: [],
      toolbarHeight: 56,
      selectedNode: null,
      editorNode: null,
      isNewNode: false,
      expandedL1: {},
      expandedL2: {},
      deletePopup: {
        visible: false,
        message: "",
      },
    };
  },

  computed: {
    ...mapGetters({ viewHeight: "ui/getHeightForDataGrid" }),

    layoutHeight() {
      if (!this.viewHeight) return null;
      return `${this.viewHeight - this.toolbarHeight}px`;
    },

    rootNodes() {
      return this.allItems
        .filter((m) => !m.ParentMenuItemId)
        .sort((a, b) => a.Sequence - b.Sequence);
    },

    parentOptions() {
      const level = this.nodeLevel(this.editorNode);
      if (level === 1) {
        return [{ value: null, label: "— root —" }];
      }
      if (level === 2) {
        const l1Options = this.rootNodes.map((m) => ({ value: m.MenuItemId, label: m.Label }));
        return [{ value: null, label: "— root —" }, ...l1Options];
      }
      // L3: parent must be an L2 (items that have a parent themselves)
      const l2Nodes = this.allItems.filter(
        (m) => m.ParentMenuItemId && !this.childrenOf(m.MenuItemId).some((c) => !c.FeatureCode)
      );
      return l2Nodes.map((m) => ({ value: m.MenuItemId, label: m.Label }));
    },
  },

  methods: {
    // ── Data ────────────────────────────────────────────────────────

    load() {
      this.showLoading(true);
      Promise.all([
        MenuManagementService.getAll(),
        MenuManagementService.getFeatureCodes(),
      ])
        .then(([menuResponse, featureCodesResponse]) => {
          this.allItems = menuResponse.data;
          this.featureCodes = featureCodesResponse.data;
          // Auto-expand all L1 nodes on initial load
          this.rootNodes.forEach((n) => {
            this.$set(this.expandedL1, n.MenuItemId, true);
          });
        })
        .catch((error) => {
          this.displayServerError(error, "Failed to load menu items");
        })
        .finally(() => {
          this.showLoading(false);
        });
    },

    // ── Tree helpers ─────────────────────────────────────────────────

    childrenOf(parentId) {
      return this.allItems
        .filter((m) => m.ParentMenuItemId === parentId)
        .sort((a, b) => a.Sequence - b.Sequence);
    },

    nodeLevel(node) {
      if (!node) return 0;
      if (!node.ParentMenuItemId) return 1;
      const parent = this.allItems.find((m) => m.MenuItemId === node.ParentMenuItemId);
      if (!parent || !parent.ParentMenuItemId) return 2;
      return 3;
    },

    l1Expanded(node) {
      return !!this.expandedL1[node.MenuItemId];
    },

    l2Expanded(node) {
      return !!this.expandedL2[node.MenuItemId];
    },

    toggleL1(node) {
      this.$set(this.expandedL1, node.MenuItemId, !this.expandedL1[node.MenuItemId]);
    },

    toggleL2(node) {
      this.$set(this.expandedL2, node.MenuItemId, !this.expandedL2[node.MenuItemId]);
    },

    featureCodeDisplay(item) {
      if (!item) return "";
      return item.DisplayName ? `${item.DisplayName} (${item.Code})` : item.Code;
    },

    childRenderMode(node) {
      const children = this.childrenOf(node.MenuItemId);
      if (!children.length) return "No children";
      return children.some((c) => c.FeatureCode) ? "Tabs" : "Sub-nav items";
    },

    // ── Selection / editing ──────────────────────────────────────────

    selectNode(node) {
      this.selectedNode = node;
      this.editorNode = { ...node };
      this.isNewNode = false;
    },

    openAddNode(parentNode) {
      this.selectedNode = parentNode || null;
      this.isNewNode = true;
      this.editorNode = {
        MenuItemId: null,
        ParentMenuItemId: parentNode ? parentNode.MenuItemId : null,
        Label: "",
        Icon: "",
        Path: "",
        FeatureCode: "",
        Sequence: this.nextSequence(parentNode ? parentNode.MenuItemId : null),
        Inactive: "N",
      };
    },

    nextSequence(parentId) {
      const siblings = this.childrenOf(parentId);
      if (!siblings.length) return 1;
      return Math.max(...siblings.map((s) => s.Sequence)) + 1;
    },

    cancelEdit() {
      this.editorNode = null;
      this.isNewNode = false;
    },

    onActiveToggle(e) {
      this.editorNode.Inactive = e.value ? "N" : "Y";
    },

    // ── Validation ───────────────────────────────────────────────────

    validateNode(node) {
      if (!node.Label || !node.Label.trim()) {
        this.$notify("Label is required.", "error");
        return false;
      }

      const level = this.nodeLevel(node);

      if (level > 3) {
        this.$notify("Maximum depth is 3 levels (L1 → L2 → Tab).", "error");
        return false;
      }

      // Warn on duplicate FeatureCode assignment
      if (node.FeatureCode) {
        const duplicate = this.allItems.find(
          (m) => m.FeatureCode === node.FeatureCode && m.MenuItemId !== node.MenuItemId
        );
        if (duplicate) {
          this.$notify(
            `Feature code "${node.FeatureCode}" is already assigned to "${duplicate.Label}".`,
            "warning"
          );
          // warn only, don't block
        }
      }

      // Mixed children check (container vs leaf)
      if (!this.isNewNode) {
        const children = this.childrenOf(node.MenuItemId);
        if (children.length > 0) {
          const hasContainers = children.some((c) => !c.FeatureCode);
          const hasLeafs = children.some((c) => c.FeatureCode);
          if (hasContainers && hasLeafs) {
            this.$notify(
              "Container nodes cannot have a mix of sub-nav items and tab items as children.",
              "error"
            );
            return false;
          }
        }
      }

      return true;
    },

    // ── Save ─────────────────────────────────────────────────────────

    saveNode() {
      const node = this.editorNode;
      if (!this.validateNode(node)) return;

      const payload = {
        Label: node.Label.trim(),
        ParentMenuItemId: node.ParentMenuItemId || null,
        Icon: node.Icon || null,
        Path: node.Path || null,
        FeatureCode: node.FeatureCode || null,
        Sequence: node.Sequence || 0,
        Inactive: node.Inactive || "N",
      };

      this.showLoading(true);

      const request = this.isNewNode
        ? MenuManagementService.create(payload)
        : MenuManagementService.update(node.MenuItemId, { ...payload, MenuItemId: node.MenuItemId });

      request
        .then((response) => {
          this.$notify(
            this.isNewNode ? "Menu item created." : "Menu item updated.",
            "success"
          );
          // Update local list without full reload
          if (this.isNewNode) {
            this.allItems.push(response.data);
          } else {
            const idx = this.allItems.findIndex((m) => m.MenuItemId === node.MenuItemId);
            if (idx !== -1) this.$set(this.allItems, idx, response.data);
          }
          this.selectedNode = response.data;
          this.editorNode = { ...response.data };
          this.isNewNode = false;
        })
        .catch((error) => {
          this.displayServerError(error, "Failed to save menu item");
        })
        .finally(() => {
          this.showLoading(false);
        });
    },

    // ── Delete ───────────────────────────────────────────────────────

    confirmDelete() {
      const node = this.editorNode;
      const children = this.childrenOf(node.MenuItemId);
      const activeChildren = children.filter((c) => c.Inactive !== "Y");

      if (activeChildren.length > 0) {
        this.deletePopup.message = `"${node.Label}" has ${activeChildren.length} active child item(s). Deleting it will also deactivate those items. Continue?`;
      } else {
        this.deletePopup.message = `Are you sure you want to delete "${node.Label}"?`;
      }
      this.deletePopup.visible = true;
    },

    deleteNode() {
      const nodeId = this.editorNode.MenuItemId;
      this.deletePopup.visible = false;
      this.showLoading(true);

      MenuManagementService.remove(nodeId)
        .then(() => {
          this.$notify("Menu item deleted.", "success");
          this.allItems = this.allItems.filter((m) => m.MenuItemId !== nodeId);
          this.editorNode = null;
          this.selectedNode = null;
          this.isNewNode = false;
        })
        .catch((error) => {
          this.displayServerError(error, "Failed to delete menu item");
        })
        .finally(() => {
          this.showLoading(false);
        });
    },

    // ── Drag-and-drop reorder ────────────────────────────────────────

    onDragEnd(parentId, event) {
      const { oldIndex, newIndex } = event;
      if (oldIndex === newIndex) return;

      const siblings = parentId === null
        ? [...this.rootNodes]
        : [...this.childrenOf(parentId)];

      const [moved] = siblings.splice(oldIndex, 1);
      siblings.splice(newIndex, 0, moved);

      const updates = siblings.map((item, i) => ({ ...item, Sequence: i + 1 }));

      updates.forEach((updated) => {
        const idx = this.allItems.findIndex((m) => m.MenuItemId === updated.MenuItemId);
        if (idx !== -1) this.$set(this.allItems, idx, updated);
      });

      MenuManagementService.reorder(
        updates.map((u) => ({ MenuItemId: u.MenuItemId, Sequence: u.Sequence }))
      ).catch((error) => {
        this.displayServerError(error, "Failed to save order");
        this.load();
      });
    },

    // ── Navigation ───────────────────────────────────────────────────

    backButtonFunction() {
      this.$router.push({ path: "/settings/user" });
    },
  },

  mounted() {
    this.$nextTick(() => {
      const toolbar = this.$el.querySelector(".view-toolbar");
      if (toolbar) this.toolbarHeight = toolbar.clientHeight;
    });
  },

  created() {
    this.load();
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/variables.additional.scss";

$mm-bg: #ffffff;
$mm-hover-bg: #f5f5f5;
$mm-selected-bg: #e3f2fd;
$mm-selected-color: #0288d1;
$mm-info-bg: #f9f9f9;

.mm-layout {
  display: flex;
  overflow: hidden;
  gap: 0;
}

// ── Tree panel ──────────────────────────────────────────────────────

.mm-tree-panel {
  width: 320px;
  min-width: 260px;
  background: $mm-bg;
  border-right: 1px solid $base-border-color;
  overflow-y: auto;
  padding: 12px 0;
  flex-shrink: 0;
}

.mm-tree-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
  padding: 0 16px 8px;
}

.mm-empty {
  padding: 24px 16px;
  color: #999;
  font-size: 13px;
}

.mm-l1-block {
  margin-bottom: 2px;
}

.mm-node {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  cursor: pointer;
  border-radius: 4px;
  margin: 1px 6px;
  font-size: 13px;
  transition: background 0.1s;

  &:hover {
    background: $mm-hover-bg;
  }

  &--selected {
    background: $mm-selected-bg;
    color: $mm-selected-color;
  }

  &--inactive {
    opacity: 0.55;
  }

  &--l1 {
    font-weight: 600;
    font-size: 13px;
  }

  &--l2 {
    padding-left: 28px;
    font-weight: normal;
  }

  &--l3 {
    padding-left: 48px;
    font-size: 12px;
    color: #666;
  }
}

.mm-drag-handle {
  font-size: 14px;
  color: transparent;
  cursor: grab;
  flex-shrink: 0;
  transition: color 0.1s;
}

.mm-node:hover .mm-drag-handle {
  color: #ccc;
}

.mm-node:hover .mm-drag-handle:hover {
  color: $base-accent;
}

.sortable-ghost {
  opacity: 0.4;
  background: $mm-selected-bg;
}

.mm-chevron {
  font-size: 12px;
  color: #aaa;
  flex-shrink: 0;
  cursor: pointer;

  &:hover {
    color: $base-accent;
  }
}

.mm-node-icon {
  font-size: 14px;
  color: #888;
  flex-shrink: 0;
}

.mm-node-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mm-node-seq {
  font-size: 11px;
  color: #bbb;
  flex-shrink: 0;
}

.mm-badge {
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 3px;
  flex-shrink: 0;

  &--inactive {
    background: #fde8e8;
    color: #c62828;
  }

  &--feature {
    background: #e8f0fe;
    color: #1a73e8;
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

// ── Editor panel ─────────────────────────────────────────────────────

.mm-editor-panel {
  flex: 1;
  background: $mm-bg;
  overflow-y: auto;
  padding: 24px;
}

.mm-editor-empty {
  color: #999;
  font-size: 13px;
  margin-top: 40px;
  text-align: center;
}

.mm-editor-title {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
}

.mm-editor-form {
  max-width: 480px;
}

.mm-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 14px;

  &--inline {
    flex-direction: row;
    align-items: center;
    gap: 12px;
  }
}

.mm-label {
  font-size: 12px;
  color: #555;
  font-weight: 500;
}

.mm-hint {
  font-weight: normal;
  color: #aaa;
  margin-left: 4px;
}

.mm-required {
  color: #d32f2f;
}

.mm-render-info {
  flex-direction: row;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: $mm-info-bg;
  border-radius: 4px;
  border: 1px solid $base-border-color;
}

.mm-render-badge {
  font-size: 12px;
  font-weight: 600;
  color: $base-accent;
}

.mm-editor-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid $base-border-color;
}

// ── Delete confirm popup ──────────────────────────────────────────────

.mm-confirm-body {
  padding: 16px;

  p {
    margin: 0 0 16px;
    font-size: 13px;
    color: #333;
  }
}

.mm-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>

<style lang="scss">
.view-toolbar .dx-button-default.dx-button-mode-contained .dx-button-content .dx-icon::before {
  color: #fff !important;
}
</style>
